import React, { useRef } from 'react';
import { IProjectSchema } from '@/api/project';
import { ButtonAssign } from '@/components/project/ButtonAssign';
import { CreateButton } from '@/components/shared/button/CreateButton';
import {
  ExtendedTableLowCode,
  ExtendedTableLowCodeRef,
} from '@/components/shared/table/ExtendedTableLowCode';
import { useCustomSpec } from '@/utils/hooks/useCustomSpec';
import { IFilterSpec } from '@/components/shared/filter/TableFilterPanel';
import { DeleteProject } from '@/components/project/DeleteProject';
import { ExportProject } from '@/components/project/ExportProject';
import { LinkButton } from '@/components/shared/button/LinkButton';
import { BiSolidShow } from 'react-icons/bi';
import { FaPen } from 'react-icons/fa6';
import { CustomCellProperties } from 'alurkerja-ui';
import moment from 'moment';
import { useIsExternal } from '@/utils/hooks/useUserType';
import { ExternalAccessBanner } from '@/components/shared/ExternalAccessBanner';

interface CustomActionCellProps {
  data: IProjectSchema;
}

export const ListProject: React.FC = () => {
  const ref = useRef<ExtendedTableLowCodeRef>(null);

  const isExternal = useIsExternal();

  const spec = useCustomSpec('/api/crud/project', {
    columns: [
      { key: 'title', label: 'Title' },
      { key: 'test_cases_count', label: 'Test Case' },
      { key: 'created_at', label: 'Created Date' },
    ],
  });

  const filterSpec: IFilterSpec[] = [
    { name: 'title', label: 'Title', type: 'text' },
    { name: 'created_at', label: 'Created Date', type: 'daterange' },
  ];

  const customCell = ({
    name,
    value,
    defaultCell,
  }: CustomCellProperties): React.ReactElement => {
    if (name === 'created_at') {
      return <>{moment(value).format('DD MMMM YYYY')}</>;
    }

    return <>{defaultCell}</>;
  };

  const customButtonCreate = () =>
    !isExternal ? (
      <div className="flex flex-row gap-3">
        <ExportProject tableRef={ref} />
        <CreateButton label="Add Project" />
      </div>
    ) : (
      <></>
    );

  const CustomActionCell: React.FC<CustomActionCellProps> = ({ data }) => {
    return (
      <>
        <LinkButton
          path={data.id}
          icon={<BiSolidShow size={20} />}
        />
        {!isExternal && (
          <>
            <ButtonAssign
              assigneeId={null}
              data={data}
              onSuccess={() => ref.current?.refetch()}
            />

            <LinkButton
              path={`${data.id}/edit`}
              icon={<FaPen size={15} />}
            />

            <DeleteProject
              id={data.id!}
              onSuccess={() => ref.current?.refetch()}
            />
          </>
        )}
      </>
    );
  };

  return (
    <div className="flex flex-col gap-4">
      {isExternal && <ExternalAccessBanner />}
      <ExtendedTableLowCode
        ref={ref}
        spec={spec}
        defaultSort="created_at"
        defaultSortOrder="desc"
        customCell={customCell}
        customActionCell={data => (
          <CustomActionCell data={data as IProjectSchema} />
        )}
        customButtonCreate={customButtonCreate}
        filterSpec={filterSpec}
        showFilter
      />
    </div>
  );
};
